(() => {
  // Repeated asset tags and Theme Editor reloads share one initializer.
  if (window.naokoLondonCalling) {
    window.naokoLondonCalling.init(document);
    return;
  }

  const instances = new WeakMap();
  function initImages(root, signal) {
    const dialog = root.querySelector('[data-nlc-lightbox]');
    const image = dialog.querySelector('[data-nlc-lightbox-image]');
    const counter = dialog.querySelector('[data-nlc-lightbox-count]');
    const previous = dialog.querySelector('[data-nlc-previous]');
    const next = dialog.querySelector('[data-nlc-next]');
    // Empty blocks and marquee clones never become independent gallery entries.
    const frames = Array.from(root.querySelectorAll('[data-nlc-image]'))
      .filter((frame) => !frame.hasAttribute('data-nlc-clone') && frame.querySelector('img'));
    let position = 0;
    let opener;

    function show(index) {
      if (!frames.length) return;
      position = (index + frames.length) % frames.length;
      const source = frames[position].querySelector('img');
      image.src = source.currentSrc || source.src;
      image.alt = source.alt;
      counter.textContent = frames.length > 1 ? `${position + 1} / ${frames.length}` : '';
      previous.hidden = next.hidden = frames.length < 2;
    }

    function close() {
      dialog.close();
      image.removeAttribute('src');
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    }

    root.addEventListener('click', (event) => {
      const frame = event.target.closest('[data-nlc-image]');
      if (!frame || !root.contains(frame) || !frame.querySelector('img')) return;
      const original = frame.hasAttribute('data-nlc-clone')
        ? frames[Number(frame.dataset.nlcClone)] : frame;
      show(Math.max(0, frames.indexOf(original)));
      opener = frame;
      dialog.showModal();
    }, { signal });
    dialog.querySelector('[data-nlc-close]').addEventListener('click', close, { signal });
    previous.addEventListener('click', () => show(position - 1), { signal });
    next.addEventListener('click', () => show(position + 1), { signal });
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); }, { signal });
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); }, { signal });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        show(position + (event.key === 'ArrowLeft' ? -1 : 1));
      }
    }, { signal });
    return () => { if (dialog.open) dialog.close(); };
  }

  function initGallery(root) {
    const track = root.querySelector('.tor');
    const originals = Array.from(track.children);
    if (originals.length < 2) track.style.animation = 'none';
    else originals.forEach((frame, index) => {
      const clone = frame.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.nlcClone = index;
      Array.from(clone.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify')) clone.removeAttribute(attribute.name);
      });
      track.appendChild(clone);
    });
    return () => track.querySelectorAll('[data-nlc-clone]').forEach((clone) => clone.remove());
  }

  function initDetail(root, signal) {
    const cards = Array.from(root.querySelectorAll('.karta'));
    const counter = root.querySelector('[data-nlc-counter]');
    let position = 0;
    let timer;
    const number = (n) => String(n).padStart(2, '0');

    function show(index) {
      if (!cards.length) { counter.textContent = ''; return; }
      position = (index + cards.length) % cards.length;
      cards.forEach((card, i) => {
        const place = (i - position + cards.length) % cards.length;
        card.classList.remove('p0', 'p1', 'p2');
        if (place < 3) card.classList.add(`p${place}`);
      });
      counter.textContent = cards.length > 1 ? `${number(position + 1)} / ${number(cards.length)}` : '';
    }

    show(0);
    if (cards.length > 1 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      timer = setInterval(() => {
        if (!root.querySelector('[data-nlc-lightbox]').open) show(position + 1);
      }, 4000);
    }
    cards.forEach((card, index) => card.addEventListener('click', (event) => {
      if (card.classList.contains('p0')) return;
      event.stopPropagation();
      clearInterval(timer);
      show(index);
    }, { capture: true, signal }));
    root.addEventListener('shopify:block:select', (event) => {
      const index = cards.indexOf(event.target.closest('.karta'));
      if (index >= 0) { clearInterval(timer); show(index); }
    }, { signal });
    return () => clearInterval(timer);
  }

  // Poziome tory (stylizacje miejsc, karty produktow): obrazki z loading=lazy poza ekranem w bok telefon doczytuje
  // dopiero przy przewijaniu toru (puste kadry). Gdy tor zbliza sie do ekranu w pionie, ladujemy od razu caly tor.
  function initRail(root) {
    const eager = () => root.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    if (!('IntersectionObserver' in window)) { eager(); return () => {}; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { eager(); io.disconnect(); }
    }, { rootMargin: '800px 0px' });
    io.observe(root);
    return () => io.disconnect();
  }

  const selector = '[data-nlc-gallery], [data-nlc-detail], [data-nlc-rail]';
  function rootsWithin(container) {
    return [...(container.matches?.(selector) ? [container] : []), ...container.querySelectorAll(selector)];
  }
  function init(container) {
    rootsWithin(container).forEach((root) => {
      if (instances.has(root)) return;
      const abort = new AbortController();
      const cleanups = [];
      if (root.hasAttribute('data-nlc-gallery')) cleanups.push(initGallery(root));
      if (root.hasAttribute('data-nlc-detail')) cleanups.push(initDetail(root, abort.signal));
      if (root.hasAttribute('data-nlc-images')) cleanups.push(initImages(root, abort.signal));
      if (root.hasAttribute('data-nlc-rail')) cleanups.push(initRail(root));
      instances.set(root, () => {
        abort.abort();
        cleanups.forEach((cleanup) => cleanup());
        instances.delete(root);
      });
    });
  }
  window.naokoLondonCalling = { init };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => instances.get(root)?.());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
